import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';

import { Subscription } from 'rxjs';

import * as moment from 'moment';

import {
  enrolTiimStudyParticipant,
  sendTiimStudyParticipantNotification,
  setSingleTiimStudyParticipantPageVisibility,
} from '../../../../../state/study-participant/tiim-study-participant/tiim-study-participant.actions';
import { AppState } from '../../../../../../state';
import { selectSelectedTiimStudyParticipant } from '../../../../../state/study-participant/tiim-study-participant/tiim-study-participant.selectors';
import { Participant } from '../../../../../../models/participant';
import { selectInterventions } from '../../../../../state/intervention/intervention.selectors';
import { Intervention } from '../../../../../../models/intervention';
import { TiimStudyParticipant } from '../../../../../../models/tiim-study-participant';
import { API_DATE_FORMAT, PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../../../../core/dashboard-constants';
import { PrivilegeService } from '../../../../../services/privilege/privilege.service';
import { Study } from '../../../../../../models/study';
import { selectSelectedStudy } from '../../../../../state/study/study.selectors';
import { MatDialog } from '@angular/material/dialog';
import { InterventionSwitchDialogComponent } from '../../../../components/intervention-switch-dialog/intervention-switch-dialog.component';
import { openDashboardConfirmationDialog } from '../../../../../state/dashboard.actions';
import { deleteParticipantFromSystemAndRemoveData } from '../../../../../state/um-participant/um-participant.actions';

const TIIM_STUDY_PARTICIPANT_PAGE_TABS = {
  PARTICIPANT_DETAILS: 'participant details',
  SCREENING: 'screening',
  SEND_NOTIFICATION: 'send notification',
};

const TIIM_STUDY_PARTICIPANT_OPTIONS = {
  NO_ACTION: 'no action',
  REJECT: 'reject',
};

const TIIM_STUDY_PARTICIPANT_START = {
  INTERVENTION_STARTS: 'intervention starts',
  CUSTOM_DATE: 'custom date',
};

@Component({
  selector: 'dashboard-tiim-study-participant',
  templateUrl: './tiim-study-participant.component.html',
  styleUrls: ['./tiim-study-participant.component.scss'],
})
export class TiimStudyParticipantComponent implements OnInit, OnDestroy {
  TIIM_STUDY_PARTICIPANT_PAGE_TABS = TIIM_STUDY_PARTICIPANT_PAGE_TABS;
  TIIM_STUDY_PARTICIPANT_OPTIONS = TIIM_STUDY_PARTICIPANT_OPTIONS;
  TIIM_STUDY_PARTICIPANT_START = TIIM_STUDY_PARTICIPANT_START;

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;

  tab = TIIM_STUDY_PARTICIPANT_PAGE_TABS.PARTICIPANT_DETAILS;

  subscriptions = new Subscription();

  selectedTiimStudyParticipant: TiimStudyParticipant;
  selectedParticipant: Participant;
  oldOption: string | number;
  oldStart: string;
  oldCustomDate: string;

  notificationForm: FormGroup;
  optionsForm: FormGroup;

  selectedStudy: Study;

  interventions: Intervention[];

  now = moment(new Date());

  constructor(
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    public privilegeService: PrivilegeService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.store.select(selectSelectedTiimStudyParticipant).subscribe((selectedTiimStudyParticipant) => {
        this.selectedTiimStudyParticipant = selectedTiimStudyParticipant;
        this.selectedParticipant = selectedTiimStudyParticipant.participant;

        if (!this.selectedTiimStudyParticipant.interventionId && this.selectedTiimStudyParticipant.rejected) {
          this.oldOption = this.TIIM_STUDY_PARTICIPANT_OPTIONS.REJECT;
        } else if (!this.selectedTiimStudyParticipant.interventionId && !this.selectedTiimStudyParticipant.rejected) {
          this.oldOption = this.TIIM_STUDY_PARTICIPANT_OPTIONS.NO_ACTION;
        } else {
          this.oldOption = this.selectedTiimStudyParticipant.interventionId;
        }

        this.oldStart = this.selectedTiimStudyParticipant.delayedStart
          ? this.TIIM_STUDY_PARTICIPANT_START.CUSTOM_DATE
          : this.TIIM_STUDY_PARTICIPANT_START.INTERVENTION_STARTS;
        this.oldCustomDate = this.selectedTiimStudyParticipant.delayedStart
          ? this.selectedTiimStudyParticipant.delayedStart
          : this.now;
      })
    );

    this.subscriptions.add(
      this.store.select(selectInterventions).subscribe((interventions) => (this.interventions = interventions))
    );

    this.subscriptions.add(
      this.store.select(selectSelectedStudy).subscribe((selectedStudy) => (this.selectedStudy = selectedStudy))
    );

    this.notificationForm = this.formBuilder.group({
      message: ['', [Validators.required, Validators.maxLength(100)]],
    });

    this.optionsForm = this.formBuilder.group({
      chosenOption: [this.oldOption],
      chosenStart: [this.oldStart],
      customDate: [this.oldCustomDate, [Validators.required]],
    });
  }

  onParticipantTabChange(event): void {
    this.tab = event?.tab?.textLabel?.toString().toLowerCase();
  }

  backToParticipants(): void {
    this.store.dispatch(
      setSingleTiimStudyParticipantPageVisibility({
        singleParticipantVisible: false,
      })
    );
  }

  get message(): AbstractControl {
    return this.notificationForm.get('message');
  }

  sendNotification(): void {
    if (this.notificationForm.invalid) {
      return;
    }

    this.store.dispatch(
      sendTiimStudyParticipantNotification({
        participantId: this.selectedParticipant?.id,
        message: this.notificationForm.getRawValue().message,
      })
    );
  }

  get chosenOption(): AbstractControl {
    return this.optionsForm.get('chosenOption');
  }

  get chosenStart(): AbstractControl {
    return this.optionsForm.get('chosenStart');
  }

  get customDate(): AbstractControl {
    return this.optionsForm.get('customDate');
  }

  saveParticipantOptions(): void {
    const updatedTiimStudyParticipant = {
      ...this.selectedTiimStudyParticipant,
      interventionId:
        this.chosenOption.value === this.TIIM_STUDY_PARTICIPANT_OPTIONS.NO_ACTION ||
        this.chosenOption.value === this.TIIM_STUDY_PARTICIPANT_OPTIONS.REJECT
          ? 0
          : this.chosenOption.value,
      rejected: this.chosenOption.value === this.TIIM_STUDY_PARTICIPANT_OPTIONS.REJECT,
      delayedStart:
        this.chosenStart.value === this.TIIM_STUDY_PARTICIPANT_START.CUSTOM_DATE
          ? moment(this.customDate.value).format(API_DATE_FORMAT)
          : null,
    };

    if (
      this.chosenOption.value !== this.TIIM_STUDY_PARTICIPANT_OPTIONS.NO_ACTION &&
      this.oldOption !== this.TIIM_STUDY_PARTICIPANT_OPTIONS.NO_ACTION &&
      this.chosenOption.value !== this.TIIM_STUDY_PARTICIPANT_OPTIONS.REJECT &&
      this.oldOption !== this.TIIM_STUDY_PARTICIPANT_OPTIONS.REJECT &&
      this.chosenOption.value !== this.oldOption
    ) {
      this.dialog.open(InterventionSwitchDialogComponent, {
        data: { updatedTiimStudyParticipant },
        minWidth: '30%',
        height: '265px',
      });
    } else {
      this.store.dispatch(
        enrolTiimStudyParticipant({
          tiimStudyParticipant: updatedTiimStudyParticipant,
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
